import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';
  
const CustomerTagContext = createContext();

export const useCustomerTag = () => {
  const context = useContext(CustomerTagContext);
  if (!context) {
    throw new Error('useCustomerTag must be used within a CustomerTagProvider');
  }
  return context;
};

export const CustomerTagProvider = ({ children }) => {
   
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { user,getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [customerTags, setCustomerTags] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/customer-tags');

  const getCustomerTags = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if (filters?.q) {
        query = '&q=' + filters?.q;
      } else {
        const q = searchParams.get('q') || '';
        query = '&q=' + q;
      }
      if (filters?.active) {
        query += '&active=' + filters?.active;
      } else {
        const active = searchParams.get('active') || '';
        query += '&active=' + active;
      }

      let response = await apiInstance.call('customer-tags?page=' + page + '&paginate=' + paginate + '' + query, 'GET');
      if (filters?.all) {
        query = '';
        setPaginate(false);
        response = await apiInstance.call('customer-tags');
      }
 
      if (response.status === 200) {
        setPaginate(response.data.paginate);
        setCustomerTags(response.data.customer_tags || []);
        hideLoading();
        if (filters) {
          return response.data.customer_tags;
        }
      } else {
        hideLoading();
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch customer tags'
        });
      }
    } catch (error) {
      hideLoading();
    }
  }, [handleApiError, hideLoading, paginate, getToken]);

  const getCustomerTagByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`customer-tags/${id}`, 'GET', null, true, true);

    if (response.status === 200) {
      let customerTags = { 'data': [response.data.customer_tag] };
      setCustomerTags(customerTags || []);
    }
  }, [getToken]);

  useEffect(() => {
    const handleLoad = async () => {
      const currentPath = window.location.pathname;

      if (currentPath.includes(getRoutePath('/customer-tags/edit/' + id))) {
        if (getToken()) {
          const apiInstance = Api(() => getToken());
          setApi(apiInstance);
          await getCustomerTagByIdApi(id);
        }
      }
    };

    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getCustomerTagByIdApi, searchParams]);

  useEffect(() => {
    const initializeApi = async () => {
      try {
        const apiInstance = Api(() => getToken());
        setApi(apiInstance);

        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getCustomerTags(page);
        }
      } catch (error) {
        // Handle error
      }
    };

    if (initialized) {
      initializeApi();
    }
  }, [getToken, getCustomerTags, searchParams, initialized]);

  const addCustomerTag = async (tag) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(tag).forEach(key => {
      if (tag[key] !== null) {
        formData.append(key, tag[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call('customer-tags', 'POST', formData, true, true);
      
      if (response.status === 200) {
        const customerTags = response.data.customer_tags;
        setCustomerTags(customerTags);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add customer tag'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to add customer tag'
      });
    }
  };

  const getCustomerTagById = (id) => {
    let customerTagData = customerTags?.data?.find(tag => {
      return Number(tag.id) === Number(id);
    });
    return customerTagData;
  };

  const updateCustomerTag = async (id, updatedTag) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(updatedTag).forEach(key => {
      if (updatedTag[key] !== null) {
        formData.append(key, updatedTag[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call(`customer-tags/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        const updatedCustomerTags = customerTags?.data?.map(tag =>
          Number(tag.id) === Number(id) ? {
            ...response.data.customer_tag,
          } : tag
        );
        setCustomerTags(updatedCustomerTags);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update customer tag'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update customer tag'
      });
    }
  };

  const deleteCustomerTag = async (id) => {
    if (!api) {
      return;
    }

    try {
      const response = await api.call(`customer-tags/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        navigate(getRoutePath('/customer-tags'));
        return { status: response.status };
      } else {
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete customer tag'
        });
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete customer tag'
      });
    }
  };

  return (
    <CustomerTagContext.Provider
      value={{
        customerTags,
        addCustomerTag,
        getCustomerTags: (page, filters) => getCustomerTags(page, filters),
        getCustomerTagById,
        updateCustomerTag,
        deleteCustomerTag,
        formErrors,
        paginate,
        clearErrors
      }}
    >
      {children}
    </CustomerTagContext.Provider>
  );
}; 